"use client";

import { useEffect, useState } from "react";

import classNames from "classnames";
import { ChevronDown, ChevronUp, Printer, Share2 } from "lucide-react";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { SingleRecommendation } from "@/entities/survey/types";
import { getLocalStorage, setLocalStorage } from "@/shared/lib/cookieConsent";
import { titleToId } from "@/shared/lib/text";
import { Button, ContentfulText, ShareLink, Typography } from "@/shared/ui";
import { Replacement } from "@/shared/ui/Typography/ContentfulText";
import { BearOff, pencil } from "@/shared/ui/icons";
import { meshBackgroundStyle } from "@/shared/ui/utils";

import { CaptionCards, CardCaptionContent } from "./CaptionCards";

import resultStyles from "./Result.module.css";

export const weekColors = {
    Universal: "bg-[#5162B1]",
    Week1: "bg-[#8D9ED5]",
    Week2: "bg-[#9A8BCB]",
    Week3: "bg-[#5E4FA7]",
} as const;

interface RecommendationCardProps {
    index_inner: number;
    index_outer: number;
    isCompleted?: boolean;
    isLast: boolean;
    isPaid?: boolean;
    rec: SingleRecommendation;
    replacement?: Replacement;
    showCaptions?: boolean;
}

export function RecommendationCard({
    index_inner,
    index_outer,
    isCompleted,
    isLast,
    isPaid,
    rec,
    replacement,
    showCaptions = true,
}: RecommendationCardProps) {
    const t = useTranslations("Recommendation");
    const [isExpanded, setIsExpanded] = useState(!isCompleted);
    const [isEditing, setIsEditing] = useState(false);
    const [customName, setCustomName] = useState("");
    const params = useParams();
    const surveyId = params.surveySession as string;

    useEffect(() => {
        setIsExpanded(!isCompleted);
    }, [isCompleted]);

    useEffect(() => {
        const savedName = getLocalStorage(`childName_${surveyId}`);
        if (savedName) {
            setCustomName(savedName);
        }
    }, [surveyId]);

    if (!rec.text) return null;

    const captions = rec.actionItems?.some((item) => typeof item === "string")
        ? rec.actionItems
              .map((item, index) =>
                  !!item
                      ? ({
                            color: weekColors[`Week${index + 1}` as keyof typeof weekColors],
                            text: `${t("week")} ${index + 1}`,
                            week: index + 1,
                        } as CardCaptionContent)
                      : null,
              )
              .filter((item): item is CardCaptionContent => item !== null)
        : [];

    let style = showCaptions ? {} : ({ background: "#fcfeff" } as any); // white

    const name = customName || t("roadmap_title_name_placeholder");

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newName = e.target.value;
        setCustomName(newName);
        setLocalStorage(`childName_${surveyId}`, newName);
    };

    return (
        <div
            className={classNames(
                "rounded-[20px] relative transition-opacity bg-white duration-200",
                isCompleted && " opacity-50 print:opacity-100 ",
                index_outer === 0 && " overflow-hidden ",
            )}
            id={isLast ? "additional-tips" : rec.title && titleToId(rec.title)}
            key={`recommendation_${rec.title}_${index_outer}_${index_inner}`}
            // I'm using inline style here because Tailwind doesn't work well with
            // dynamicly set color classes.
            style={style}
        >
            {showCaptions && <CaptionCards captions={captions} />}
            {index_outer === 0 && (
                <div className="sticky top-0 z-10 flex-shrink-0">
                    <div className="px-8 py-4 print:py-0" style={meshBackgroundStyle}>
                        <h2 className="print:text-black print:text-2xl text-xl font-bold text-white">
                            {t("roadmap_title_pattern").split("%name%")[0]}
                            <span className="relative inline-block">
                                {isEditing ? (
                                    <input
                                        autoFocus
                                        className="bg-transparent border-b border-white outline-none text-white text-left w-32"
                                        onBlur={() => setIsEditing(false)}
                                        onChange={handleNameChange}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === "Escape") {
                                                setIsEditing(false);
                                            }
                                        }}
                                        placeholder={t("roadmap_title_name_placeholder_text")}
                                        type="text"
                                        value={customName}
                                    />
                                ) : (
                                    <>
                                        <span
                                            className="underline decoration-dotted cursor-pointer"
                                            onClick={() => setIsEditing(true)}
                                        >
                                            {name}
                                        </span>
                                        <Image
                                            alt="Edit name"
                                            className="inline-block mx-2 -mt-2 cursor-pointer"
                                            height={26}
                                            onClick={() => setIsEditing(true)}
                                            src={pencil}
                                            width={26}
                                        />
                                    </>
                                )}
                            </span>
                            {t("roadmap_title_pattern").split("%name%")[1]}
                        </h2>
                    </div>
                </div>
            )}
            <div className="flex items-start print:p-4 p-8 max-sm:px-4">
                {isCompleted && (
                    <div className="w-8 h-8 mr-3 flex items-center justify-center">
                        <BearOff className="w-8 h-8" />
                    </div>
                )}
                <div className="flex-1">
                    {rec.title && (
                        <div
                            className="flex items-center cursor-pointer mb-4"
                            onClick={() => index_outer > 0 && setIsExpanded(!isExpanded)}
                        >
                            <Typography
                                className="text-lg sm:text-xl font-semibold text-primary-100 flex-1"
                                variant="title3"
                            >
                                {rec.title}
                            </Typography>

                            {index_outer > 0 &&
                                (isExpanded ? (
                                    <ChevronUp className="w-8 h-8 text-primary-100 ml-2 print:hidden" />
                                ) : (
                                    <ChevronDown className="w-8 h-8 text-primary-100 ml-2 print:hidden" />
                                ))}
                        </div>
                    )}
                    <div
                        className={
                            resultStyles.RecommedationContent +
                            " prose text-lg max-w-none" +
                            (isExpanded ? "" : " h-0 print:h-auto print:overflow-visible overflow-hidden")
                        }
                    >
                        <ContentfulText document={rec.text} replacement={replacement} />

                        {index_outer === 0 && (
                            <div className="flex items-center pt-6 print:hidden">
                                <Button
                                    className={classNames(
                                        "!text-xl !font-normal mr-4",
                                        isPaid ? "!bg-primary-100" : "!bg-gray-500 cursor-not-allowed",
                                    )}
                                    disabled={!isPaid}
                                    onClick={() => {
                                        isPaid && window.print();
                                    }}
                                >
                                    <Printer className="w-6 h-6 mr-2" />
                                    {t("print")}
                                </Button>
                                {isPaid ? (
                                    <ShareLink>
                                        <Button className="!bg-primary-100 !text-xl !font-normal">
                                            {t("share")} <Share2 className="ml-2 w-5 h-5" />
                                        </Button>
                                    </ShareLink>
                                ) : (
                                    <Button className="!bg-gray-500 cursor-not-allowed !text-xl !font-normal" disabled>
                                        {t("share")} <Share2 className="ml-2 w-5 h-5" />
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
